import React, { useEffect, useMemo, useState } from 'react'
import { observer, inject } from 'mobx-react'
import { Card, Empty, Pagination, Tooltip, message } from 'antd'
import moment from 'moment'

// Components
import TableComponent from '../../../components/Common/TableComponent'
import UnreadText from '../../../components/UnreadText/UnreadText'
import { EmptyText } from '../../../components/Common/CellText'

// Constants
import { DATE_FORMAT_DEFAULT } from '../../../constants'

// Styled
import { TableFooterActionWrapper } from '../InternalDocumentOutgoingDocPage/InternalDocumentOutgoingDocPageStyled'
import { UndoOutlined } from '@ant-design/icons'

const OutgoingPanel = inject(
  'internalDocumentStore',
  'loadingAnimationStore'
)(
  observer(({ internalDocumentStore, loadingAnimationStore }) => {
    const [currentPage, setCurrentPage] = useState(1)
    const pageSize = 10

    const {
      internalDocumentOutgoingDeletedList,
      internalDocumentOutgoingListTotalCount,
      setQueryParamsOutgoingDeleted,
      getInternalDocumentOutgoingDeletedList,
      restoreInternalDocumentById,
    } = internalDocumentStore

    const fetchData = async () => {
      try {
        loadingAnimationStore.setTableLoading(true)
        setQueryParamsOutgoingDeleted({
          page: currentPage - 1,
          size: pageSize,
        })
        await getInternalDocumentOutgoingDeletedList()
      } catch (error) {
        message.error('Không thể tải danh sách văn bản đã xóa')
      } finally {
        loadingAnimationStore.setTableLoading(false)
      }
    }

    useEffect(() => {
      fetchData()
    }, [currentPage])

    const handleRestoreDocument = async record => {
      try {
        loadingAnimationStore.setTableLoading(true)
        await restoreInternalDocumentById(record.code)
        message.success('Khôi phục văn bản thành công')
        await getInternalDocumentOutgoingDeletedList()
      } catch (error) {
        message.error('Khôi phục văn bản thất bại')
      } finally {
        loadingAnimationStore.setTableLoading(false)
      }
    }

    const columns = useMemo(() => {
      return [
        {
          title: 'Ngày đi',
          width: 100,
          render: record => (
            <UnreadText
              text={moment(record.outgoing_date).format(DATE_FORMAT_DEFAULT)}
              width={100}
            />
          ),
        },
        {
          title: 'Số hiệu',
          width: 100,
          render: record => (
            <UnreadText text={record.document_number} width={100} />
          ),
        },
        {
          title: 'Trích yếu',
          width: 400,
          render: record => <UnreadText text={record.title} width={400} />,
        },
        {
          title: 'Cơ quan ban hành',
          width: 250,
          render: record => (
            <UnreadText text={record.authority_issued} width={250} />
          ),
        },
        {
          title: 'Người ký',
          width: 150,
          render: record =>
            record.signer ? (
              <UnreadText text={record.signer} width={150} />
            ) : (
              <EmptyText>Không có.</EmptyText>
            ),
        },
        {
          title: <div style={{ textAlign: 'center' }}>Tác vụ</div>,
          key: 'action',
          width: 80,
          fixed: 'right',
          render: record => (
            <Tooltip title="Khôi phục">
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <div
                  onClick={() => handleRestoreDocument(record)}
                  style={{
                    backgroundColor: '#fff',
                    border: '1px solid #d9d9d9',
                    borderRadius: 6,
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                  }}>
                  <UndoOutlined style={{ color: '#1890ff', fontSize: 16 }} />
                </div>
              </div>
            </Tooltip>
          ),
        },
      ]
    }, [])

    const data = internalDocumentOutgoingDeletedList || []
    const totalCount = internalDocumentOutgoingListTotalCount || 0

    return (
      <Card>
        <TableComponent
          rowKey={record => record.id}
          dataSource={data}
          columns={columns}
          pagination={false}
          scroll={{ x: 1000 }}
          loading={{
            spinning: loadingAnimationStore.tableLoading,
            tip: 'Đang tải dữ liệu...',
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Không có dữ liệu"
              />
            ),
          }}
        />

        <TableFooterActionWrapper>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={totalCount}
            showSizeChanger={false}
            showLessItems
            onChange={setCurrentPage}
            hideOnSinglePage
          />
        </TableFooterActionWrapper>
      </Card>
    )
  })
)

export default OutgoingPanel
